import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { loadArtworkTexture } from '../artwork-texture.js';
import { vdjColor, vdjBeatChroma, vdjCameraKick, VdjPost, VdjComfort, VDJ_PRESETS } from './lib/vdjLook.js';

// Contrat du rig partagé : interdit. La source est sa propre lumière.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'slitscan',
  family: 'shader',
  danceability: 0.85,
  requiresTier: 0,
  energyLevel: 0.80,
  bpmMin: 110,
  bpmMax: 175,
  movement: 0.80,
  tonality: 0.45,
  enabled: true
};

// SLITSCAN : la « fente temporelle » des films expérimentaux et de la porte
// des étoiles de 2001. Chaque pixel de l'écran montre la même petite
// animation, mais avec un RETARD qui dépend de sa place : une forme qui bouge
// s'étire en rubans, en spirales, en vagues de temps. Techno, psytrance, IDM,
// électro. Règles R1 à R19, preset party de vdjLook (R9 : 0,85).
//
// LA SOURCE (ce qu'on filme) : un seul shader plein cadre dans une petite
// cible (480 x 270 ; tier 1 : 384 x 216 ; tier 0 : 320 x 180) : 4 formes néon
// (anneau, barre, triangle, croix) en champs de distance tracés en pixels de
// la source (R1 : coeur blanc, gaine saturée, halo), qui tournent, pulsent et
// voyagent au rythme ; la pochette en carré au centre, qui tourne lentement
// et pulse sur les temps. Volontairement simple.
//
// LA MÉMOIRE DU TEMPS : un ATLAS de cases (8 x 6 pour 48 images ; tier 1 :
// 8 x 4 = 32 ; tier 0 : 6 x 4 = 24) dans une seule cible RGBA8 - pas de
// texture array : l'atlas marche en WebGL1 comme en WebGL2 (la télé), et on
// écrit une case par le viewport et le ciseau de la cible. La capture se fait
// à 30 Hz FIXES (une case toutes les 1/30 s, pas une par image : 48 cases
// font 1,6 s à 60 comme à 30 images par seconde, le Pi et le Mac montrent le
// même temps). C'est le seul rendu en plus de l'affichage.
//
// L'AFFICHAGE : pour chaque pixel, un retard d (en cases, fractionnaire) lu
// sur la carte du moment ; la position p = tête - d tombe entre deux cases :
// deux lectures, mélangées (aucune marche visible). Dans une case, la lecture
// reste à un demi-texel du bord (pas de fuite vers la voisine). Cartes, une
// par phrase : HORIZONTAL, VERTICAL, RADIAL, SPIRAL (sinus de l'angle plus le
// rayon : aucune couture), BANDS (verre cannelé : rampes alternées), NOISE
// (bruit lent qui coule). Centre de RADIAL et SPIRAL : le centre de l'écran,
// le centre de composition (R19).
//
// LE RYTHME (party)
//   TEMPS    la source pulse (c'est elle qui fait les vagues) ; séparation RGB
//   MESURE   le retard s'inverse (le passé change de côté, en un demi-temps) ;
//            à-coup de caméra (vdjCameraKick)
//   PHRASE   carte suivante, palette suivante
//   MONTÉE   l'amplitude monte jusqu'au tampon entier : tout se liquéfie
//   DROP     retard nul d'un coup (image nette et présente), flash, puis le
//            retard revient en 2 temps, en RADIAL

const TIER = {
  0: { w: 320, h: 180, cols: 6, rows: 4 },
  1: { w: 384, h: 216, cols: 8, rows: 4 },
  2: { w: 480, h: 270, cols: 8, rows: 6 }
};
export const MAPS = ['horizontal', 'vertical', 'radial', 'spiral', 'bands', 'noise'];
const CAPTURE_HZ = 30;
const BASE_AMP = 0.55;                 // l'amplitude de croisière (fraction du tampon)
const DROP_RETURN_BEATS = 2;
const NO_BPM = 128;
const DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;
const NEON = [0xff2a6d, 0x05d9e8, 0xfcee0c, 0x9d4edd, 0x39ff14, 0xff8c00, 0x00f5d4, 0xff3cf0];

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  try { return new URLSearchParams(location.search).get(name); } catch (e) { return null; }
}
// la mémoire du tampon, en octets (RGBA8)
export function bufferBytes(tier) { const L = TIER[tier]; return L.w * L.h * L.cols * L.rows * 4; }
export function tierLayout(tier) { return Object.assign({ frames: TIER[tier].cols * TIER[tier].rows }, TIER[tier]); }

// la carte de retards, en JS (copie du GLSL, pour les tests) : 0..1
export function delayMap(map, x, y, A, t) {
  const sx = (x * 2 - 1) * A, sy = y * 2 - 1, r = Math.hypot(sx, sy), rmax = Math.hypot(A, 1);
  if (map === 'horizontal') return x;
  if (map === 'vertical') return y;
  if (map === 'radial') return r / rmax;
  if (map === 'spiral') return 0.5 + 0.5 * Math.sin(Math.atan2(sy, sx) * 3 + r * 7);
  if (map === 'bands') { const n = 10, b = Math.floor(x * n), f = x * n - b; return (b % 2 ? 1 - f : f) * 0.6 + 0.4 * (b / (n - 1)); }
  return 0.5 + 0.25 * (Math.sin(sx * 2.1 + t * 0.3) * Math.sin(sy * 2.7 - t * 0.2) + Math.sin(sx * 1.3 - sy * 1.9 + t * 0.25));
}

// ------------------------------------------------------------------ la source
const SRC_VERTEX = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
const SRC_FRAGMENT = `
  precision highp float;
  uniform vec2 uSize;              // la source en pixels
  uniform vec4 uShape[4];          // x, y (unités de hauteur), rotation, échelle
  uniform vec3 uCol[4];
  uniform sampler2D uCover;
  uniform float uCoverAngle;
  uniform float uCoverScale;
  uniform float uPulse;
  varying vec2 vUv;
  mat2 rot(float a) { float c = cos(a), s = sin(a); return mat2(c, s, -s, c); }
  float sdBox(vec2 p, vec2 b) { vec2 q = abs(p) - b; return length(max(q, 0.0)) + min(max(q.x, q.y), 0.0); }
  float sdTri(vec2 p, float r) {
    const float k = 1.7320508;
    p.x = abs(p.x) - r; p.y = p.y + r / k;
    if (p.x + k * p.y > 0.0) p = vec2(p.x - k * p.y, -k * p.x - p.y) / 2.0;
    p.x -= clamp(p.x, -2.0 * r, 0.0);
    return -length(p) * sign(p.y);
  }
  // le néon (R1) : coeur blanc, gaine, halo, d en pixels de la source
  vec3 neon(float d, vec3 c) {
    float core = 1.0 - smoothstep(0.6, 1.4, d);
    float sheath = 1.0 - smoothstep(2.2, 3.2, d);
    vec3 col = c * exp(-d / 6.0) * 0.35 * (1.0 - smoothstep(24.0, 36.0, d));
    col = mix(col, c * 0.85, sheath);
    return mix(col, vec3(1.0, 0.96, 0.92) * 1.1, core);
  }
  void main() {
    vec2 px = vUv * uSize;
    vec2 p = (px - uSize * 0.5) / (uSize.y * 0.5);             // unités de hauteur, centre de la source
    float pxu = uSize.y * 0.5;                                  // pixels par unité
    vec3 col = vec3(0.0);
    // la pochette : un carré au centre, qui tourne et pulse
    vec2 q = rot(uCoverAngle) * p / uCoverScale;
    float halfSide = 0.30;
    vec2 cu = q / (2.0 * halfSide) + 0.5;
    float inside = step(abs(q.x), halfSide) * step(abs(q.y), halfSide);
    if (inside > 0.5) col = texture2D(uCover, cu).rgb * (0.75 + 0.35 * uPulse);
    // un trait fin de la première couleur, sans halo : un néon blanc autour
    // de la pochette faisait un halo gris (R3)
    float frame = abs(sdBox(q, vec2(halfSide))) * uCoverScale * pxu;
    col = max(col, uCol[0] * (1.0 - smoothstep(0.6, 1.6, frame)));
    // les formes
    ${[0, 1, 2, 3].map((k) => `{
      vec4 s = uShape[${k}];
      vec2 lp = rot(s.z) * (p - s.xy) / s.w;
      float d;
      ${k === 0 ? 'd = abs(length(lp) - 0.22);' : k === 1 ? 'd = abs(sdBox(lp, vec2(0.30, 0.035)));' : k === 2 ? 'd = abs(sdTri(lp, 0.22));' : 'd = abs(min(sdBox(lp, vec2(0.24, 0.035)), sdBox(lp, vec2(0.035, 0.24))));'}
      col = max(col, neon(d * s.w * pxu, uCol[${k}]));
    }`).join('\n    ')}
    gl_FragColor = vec4(col, 1.0);
  }
`;

// ------------------------------------------------------------------ l'affichage
const DISPLAY_FRAGMENT = `
  precision highp float;
  uniform sampler2D uAtlas;
  uniform vec2 uGrid;              // colonnes, rangées
  uniform float uFrames;           // cases écrites (<= colonnes x rangées)
  uniform float uHead;             // l'indice de la case la plus récente
  uniform float uDepth;            // le retard maximal, en cases
  uniform float uAmp;              // -1..1 (signe : le sens du passé)
  uniform float uMap;
  uniform float uMapPrev;
  uniform float uMapMix;           // fondu d'une carte à l'autre (une demi-mesure)
  uniform float uTime;
  uniform vec2 uSrcTexel;          // 1 / taille de l'atlas
  uniform vec2 uSrcSize;           // une case, en texels
  uniform float uAspect;
  uniform float uZoom;
  uniform float uRoll;
  uniform float uFlash;
  uniform float uGain;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;
  float mapAt(float m, vec2 uv, vec2 s) {
    float r = length(s), rmax = length(vec2(uAspect, 1.0));
    if (m < 0.5) return uv.x;
    if (m < 1.5) return uv.y;
    if (m < 2.5) return r / rmax;
    if (m < 3.5) return 0.5 + 0.5 * sin(atan(s.y, s.x) * 3.0 + r * 7.0);
    if (m < 4.5) { float n = 10.0, b = floor(uv.x * n), f = uv.x * n - b; return (mod(b, 2.0) > 0.5 ? 1.0 - f : f) * 0.6 + 0.4 * (b / (n - 1.0)); }
    return 0.5 + 0.25 * (sin(s.x * 2.1 + uTime * 0.3) * sin(s.y * 2.7 - uTime * 0.2) + sin(s.x * 1.3 - s.y * 1.9 + uTime * 0.25));
  }
  // la case k (entière), au point uv de la source
  vec3 slot(float k, vec2 uv) {
    float cx = mod(k, uGrid.x), cy = floor(k / uGrid.x);
    vec2 inner = clamp(uv * uSrcSize, vec2(0.5), uSrcSize - 0.5);     // à un demi-texel du bord
    return texture2D(uAtlas, (vec2(cx, cy) * uSrcSize + inner) * uSrcTexel).rgb;
  }
  void main() {
    // l'écran en unités de hauteur, centre = centre de composition (R19) ; à-coup de caméra
    vec2 s = (vUv * 2.0 - 1.0) * vec2(uAspect, 1.0);
    float c = cos(uRoll), sn = sin(uRoll);
    vec2 sc = mat2(c, sn, -sn, c) * s / uZoom;
    // la source (16:9) couvre l'écran
    float srcA = uSrcSize.x / uSrcSize.y;
    vec2 k = uAspect > srcA ? vec2(1.0, srcA / uAspect) : vec2(uAspect / srcA, 1.0);
    vec2 uv = (sc / vec2(uAspect, 1.0)) * 0.5 * k + 0.5;
    // le retard (en cases) : la carte, son sens, son amplitude
    float m = mix(mapAt(uMapPrev, vUv, s), mapAt(uMap, vUv, s), uMapMix);
    m = uAmp >= 0.0 ? m : 1.0 - m;
    float d = clamp(abs(uAmp) * m * uDepth, 0.0, uFrames - 1.0);
    // deux cases voisines, interpolées
    float pos = uHead - d;
    float a = floor(pos), f = pos - a;
    float N = uGrid.x * uGrid.y;
    float ka = mod(a + N, N), kb = mod(a + 1.0 + N, N);
    vec3 col = mix(slot(ka, uv), slot(kb, uv), f);
    col += vec3(uFlash);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float sd = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    gl_FragColor = vec4(col * uGain * mix(0.45, 1.0, smoothstep(0.0, uSafeFeather, sd)), 1.0);
  }
`;
const QUAD_VERTEX = SRC_VERTEX;

function fallbackCover(palette) {
  const src = palette && (palette.vividColors || palette.dominantColors) || [];
  const a = vdjColor(src[0] != null ? src[0] : 0xff3c8c, new THREE.Color()), b = vdjColor(src[1] != null ? src[1] : 0x3cb4ff, new THREE.Color());
  const N = 32, data = new Uint8Array(N * N * 4), c = new THREE.Color();
  for (let j = 0; j < N; j++) for (let i = 0; i < N; i++) {
    c.copy(a).lerp(b, (i + j) / (2 * N - 2));
    if (Math.hypot(i - 15.5, j - 15.5) < 7) c.lerp(new THREE.Color(1, 1, 1), 0.6);
    c.convertLinearToSRGB();
    const o = (j * N + i) * 4; data[o] = c.r * 255; data[o + 1] = c.g * 255; data[o + 2] = c.b * 255; data[o + 3] = 255;
  }
  const t = new THREE.DataTexture(data, N, N, THREE.RGBAFormat);
  t.colorSpace = THREE.SRGBColorSpace; t.minFilter = t.magFilter = THREE.LinearFilter; t.needsUpdate = true;
  return t;
}

// =============================================================================

export class SlitscanVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null && TIER[o.qualityTier] ? o.qualityTier : 2;
    this._L = tierLayout(this._tier);
    const m = (urlParam('slitmap') || '').toLowerCase();
    this._forceMap = MAPS.indexOf(m) >= 0 ? MAPS.indexOf(m) : null;
    const d = parseFloat(urlParam('slitdepth'));
    this._forceDepth = Number.isFinite(d) ? Math.max(0, Math.min(this._L.frames - 1, d)) : null;
    this._sourceOnly = urlParam('slitsource') === '1';
    this._post = new VdjPost({ bloomStrength: 0.20, bloomThreshold: 0.85, grain: 0.0, vignette: 0.30, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 });
    this._comfort = new VdjComfort(VDJ_PRESETS.party.comfort);
    if (urlParam('slitprobe') === '1' && typeof window !== 'undefined') window.__slitscan = this;
  }

  get postProcessing() { return this._post.value; }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    this._prevBackground = scene.background;
    scene.background = new THREE.Color(0x000000);
    const L = this._L;
    // l'atlas : la mémoire du temps
    this._atlas = new THREE.WebGLRenderTarget(L.w * L.cols, L.h * L.rows, { depthBuffer: false, stencilBuffer: false, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, generateMipmaps: false });
    this._fallback = fallbackCover(null);
    this._SU = {
      uSize: { value: new THREE.Vector2(L.w, L.h) }, uShape: { value: Array.from({ length: 4 }, () => new THREE.Vector4(0, 0, 0, 1)) },
      uCol: { value: Array.from({ length: 4 }, () => new THREE.Vector3(1, 1, 1)) }, uCover: { value: this._fallback },
      uCoverAngle: { value: 0 }, uCoverScale: { value: 1 }, uPulse: { value: 0 }
    };
    this._srcScene = new THREE.Scene();
    this._srcCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    const sq = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._SU, vertexShader: SRC_VERTEX, fragmentShader: SRC_FRAGMENT, depthTest: false, depthWrite: false }));
    sq.frustumCulled = false; this._srcScene.add(sq); this._srcQuad = sq;
    this._DU = {
      uAtlas: { value: this._atlas.texture }, uGrid: { value: new THREE.Vector2(L.cols, L.rows) }, uFrames: { value: 1 }, uHead: { value: 0 },
      uDepth: { value: L.frames - 1 }, uAmp: { value: BASE_AMP }, uMap: { value: 0 }, uMapPrev: { value: 0 }, uMapMix: { value: 1 }, uTime: { value: 0 },
      uSrcTexel: { value: new THREE.Vector2(1 / (L.w * L.cols), 1 / (L.h * L.rows)) }, uSrcSize: { value: new THREE.Vector2(L.w, L.h) },
      uAspect: safeZoneUniforms.uAspect, uZoom: { value: 1 }, uRoll: { value: 0 }, uFlash: { value: 0 }, uGain: { value: 1 },
      uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    };
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({ uniforms: this._DU, vertexShader: QUAD_VERTEX, fragmentShader: DISPLAY_FRAGMENT, depthTest: false, depthWrite: false }));
    this._display.frustumCulled = false; this._display.renderOrder = -1000;
    const self = this;
    this._display.onBeforeRender = function (renderer) { self._capture(renderer); };
    scene.add(this._display);

    // l'état
    this._time = 0; this._clock = 0; this._clockPrev = 0; this._targetOffset = null;
    this._beatCount = 0; this._barCount = 0; this._phaseBeat = null; this._phaseBar = null; this._phasePhrase = null;
    this._bpm = NO_BPM; this._buildup = 0;
    this._captureClock = 0; this._due = 1; this._written = 0; this._head = -1;
    this._map = this._forceMap != null ? this._forceMap : 0; this._mapPrev = this._map; this._mapAt = -99;
    this._sign = 1; this._signFrom = 1; this._signAt = -99;
    this._pal = 0; this._accent = null; this._paletteKey = undefined;
    this._drop = null; this._lastMode = null; this._flash = 0; this._pulse = 0;
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null;
    this._artUrl = undefined; this._coverTex = null;
    this._stats = { captures: 0, drops: 0 };
    this._applyColours();
  }

  _applyPalette(pal) {
    const v = pal && (pal.vividColors || pal.dominantColors);
    const key = v && v.length ? String(v[0]) : '';
    if (key === this._paletteKey) return;
    this._paletteKey = key; this._accent = v && v.length ? v[0] : null;
    this._applyColours();
  }
  _applyColours() {
    for (let k = 0; k < 4; k++) {
      const hex = k === 0 && this._accent != null ? this._accent : NEON[(this._pal * 3 + k * 2) % NEON.length];
      const c = vdjColor(hex, new THREE.Color(), 0.92);
      this._SU.uCol.value[k].set(c.r, c.g, c.b);
    }
  }
  _cover(profile) {
    const url = profile.artworkUrl || null;
    if (url === this._artUrl) return;
    this._artUrl = url;
    if (this._fallback) this._fallback.dispose();
    this._fallback = fallbackCover(profile.artworkPalette);
    if (!this._coverTex) this._SU.uCover.value = this._fallback;
    if (!url) { if (this._coverTex) { this._coverTex.dispose(); this._coverTex = null; } this._SU.uCover.value = this._fallback; return; }
    loadArtworkTexture(url, { wrap: THREE.ClampToEdgeWrapping, mipmaps: true }, (tex) => {
      if (!this._scene || url !== this._artUrl) { tex.dispose(); return; }
      if (this._coverTex) this._coverTex.dispose();
      this._coverTex = tex; this._SU.uCover.value = tex;
    }, () => { if (this._scene && url === this._artUrl) this._SU.uCover.value = this._fallback; });
  }

  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._drops.push(m.atMs);
      this._drops.sort((a, b) => a - b); this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) {
      this._dropIdx = 0; while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) this._dropIdx++;
    }
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) { if (now - this._drops[this._dropIdx] < DROP_STALE_MS) fired = true; this._dropIdx++; }
    return fired;
  }

  update(deltaTime, profile) {
    const dt = Math.min(Math.max(deltaTime, 0), 0.1);
    this._applyPalette(profile.artworkPalette);
    this._cover(profile);
    this._paused = profile.playbackState !== 'PLAYING';
    if (this._paused) return;                     // pause : la source et le tampon gèlent
    this._time += dt;

    // l'horloge, en temps
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    const bp = profile.beatPhase, barIn = profile.barPhase;
    const hasGrid = bp != null && bpm > 0;
    this._bpm = hasGrid ? THREE.MathUtils.clamp(bpm, 60, 200) : NO_BPM;
    this._clockPrev = this._clock;
    if (hasGrid) {
      if (this._phaseBeat != null && bp < this._phaseBeat - 0.5) this._beatCount++;
      this._phaseBeat = bp;
      let target;
      if (barIn != null) {
        if (this._phaseBar != null && barIn < this._phaseBar - 0.5) this._barCount++;
        this._phaseBar = barIn;
        target = 4 * (this._barCount + barIn);
      } else target = this._beatCount + bp;
      if (this._targetOffset == null) this._targetOffset = 4 * Math.round((this._clock - target) / 4);
      const err = target + this._targetOffset - this._clock;
      if (Math.abs(err) > 2) { this._targetOffset = 4 * Math.round((this._clock - target) / 4); this._clock = target + this._targetOffset; }
      else { const rate = this._bpm / 60; this._clock += Math.max(dt * rate * 0.5, dt * rate + err * Math.min(1, dt / 0.5)); }
    } else { this._clock += dt * this._bpm / 60; this._targetOffset = null; }
    const clock = this._clock;
    const beat = Math.floor(clock) !== Math.floor(this._clockPrev);
    const bar = Math.floor(clock / 4) !== Math.floor(this._clockPrev / 4);
    let phrase = false;
    const ph = profile.phrasePhase;
    if (ph != null) { if (this._phasePhrase != null && ph < this._phasePhrase - 0.5) phrase = true; this._phasePhrase = ph; }
    else phrase = Math.floor(clock / 16) !== Math.floor(this._clockPrev / 16);

    const director = profile.director;
    const next = director && director.nextEvent;
    let build = 0;
    if (next && next.type === 'drop' && next.msUntil < 8000) build = 1 - next.msUntil / 8000;
    if (director && director.mode === 'build') build = Math.max(build, 0.6);
    this._buildup += (build - this._buildup) * (1 - Math.exp(-dt / 0.8));

    // DROP : retard nul d'un coup, flash, puis retour en 2 temps en RADIAL
    let drop = hasGrid && this._tickDrops(profile);
    if (director && director.mode === 'drop' && this._lastMode !== 'drop' && director.hasReliableGrid) drop = true;
    this._lastMode = director ? director.mode : null;
    if (drop && (!this._drop || clock - this._drop.start > 2)) {
      this._drop = { start: clock };
      this._mapPrev = this._map = MAPS.indexOf('radial'); this._mapAt = -99;
      if (this._comfort.requestFlash(this._time)) this._flash = 0.85 * 0.35;
      this._stats.drops++;
    }
    if (beat) this._pulse = 1;
    if (bar && !this._drop) { this._signFrom = this._sign; this._sign = -this._sign; this._signAt = clock; }
    if (phrase && !this._drop) {
      this._pal++; this._applyColours();
      if (this._forceMap == null) { this._mapPrev = this._map; this._map = (this._map + 1) % MAPS.length; this._mapAt = clock; }
    }

    // la source : formes qui voyagent (Lissajous), tournent et pulsent
    const pulse = this._pulse;
    // (des vitesses lentes ne laissaient presque rien changer dans la fenêtre
    // de retard, moins d'une seconde : il faut que la source BOUGE ; mais une
    // rotation propre trop vive sépare deux captures de 1/30 s au point que le
    // fondu entre elles montre des copies en peigne au lieu d'un ruban)
    for (let k = 0; k < 4; k++) {
      const ph2 = k * 1.7, sp = 0.9 + 0.25 * k;
      const x = 0.95 * Math.sin(clock * sp * 0.5 + ph2), y = 0.55 * Math.sin(clock * sp * 0.37 + ph2 * 1.3);
      this._SU.uShape.value[k].set(x * 16 / 9 * 0.8, y, clock * (0.8 + 0.3 * k) * (k % 2 ? -1 : 1), 1 + 0.5 * pulse * (k % 2 ? 0.7 : 1));
    }
    this._SU.uCoverAngle.value = clock * 0.08;
    this._SU.uCoverScale.value = 1 + 0.08 * pulse;
    this._SU.uPulse.value = pulse;
    this._pulse *= Math.exp(-dt / 0.16);

    // le retard : amplitude (croisière, montée, drop), sens (mesure), carte (phrase)
    let amp = BASE_AMP + (1 - BASE_AMP) * clamp01(this._buildup);
    if (this._drop) {
      const t = clock - this._drop.start;
      amp *= easeInOut(t / DROP_RETURN_BEATS);
      if (t >= DROP_RETURN_BEATS) this._drop = null;
    }
    const sMix = easeInOut((clock - this._signAt) / 0.5);
    const sign = this._signFrom + (this._sign - this._signFrom) * sMix;
    const U = this._DU;
    U.uAmp.value = this._sourceOnly ? 0 : amp * sign;
    if (this._forceDepth != null) U.uDepth.value = this._forceDepth;
    U.uMap.value = this._map; U.uMapPrev.value = this._mapPrev; U.uMapMix.value = easeInOut((clock - this._mapAt) / 2);
    U.uTime.value = this._time;
    // à-coup de caméra sur la mesure (preset party)
    const kick = vdjCameraKick(clock / 4 - Math.floor(clock / 4), Math.floor(clock / 4), 4);
    U.uZoom.value = kick.zoom; U.uRoll.value = kick.roll;
    U.uFlash.value = this._flash; this._flash *= Math.exp(-dt / 0.10);
    const est = 0.10 + 0.5 * this._flash;
    U.uGain.value = this._comfort.measure(est, dt);
    this._post.update(vdjBeatChroma(clock - Math.floor(clock), 60 / this._bpm));
    // la capture : une case toutes les 1/30 s (au rendu)
    this._captureClock += dt;
  }

  // LA CAPTURE : une case de l'atlas, par le viewport et le ciseau de la cible
  _capture(renderer) {
    const L = this._L, N = L.frames;
    let n = 0;
    if (this._written === 0) n = 1;
    else if (!this._paused) { n = Math.min(2, Math.floor(this._captureClock * CAPTURE_HZ)); }
    if (n > 0) {
      this._captureClock = Math.max(0, this._captureClock - n / CAPTURE_HZ);
      const prev = renderer.getRenderTarget();
      for (let i = 0; i < n; i++) {
        this._head = (this._head + 1) % N;
        const cx = this._head % L.cols, cy = Math.floor(this._head / L.cols);
        this._atlas.viewport.set(cx * L.w, cy * L.h, L.w, L.h);
        this._atlas.scissor.set(cx * L.w, cy * L.h, L.w, L.h);
        this._atlas.scissorTest = true;
        renderer.setRenderTarget(this._atlas);
        renderer.render(this._srcScene, this._srcCam);
        this._written = Math.min(N, this._written + 1);
        this._stats.captures++;
      }
      renderer.setRenderTarget(prev);
    }
    // la tête de LECTURE, fractionnaire, une case derrière l'écriture : entre
    // deux captures elle glisse de l'avant-dernière vers la dernière, si bien
    // que même à retard nul l'image avance en continu (interpolée), pas par
    // à-coups de 30 Hz
    const frac = Math.min(0.999, this._captureClock * CAPTURE_HZ);   // (en pause l'horloge de capture est gelée : la tête aussi)
    this._DU.uHead.value = this._written > 1 ? this._head - 1 + frac : this._head;
    this._DU.uFrames.value = Math.max(1, this._written - 1);
  }

  getProbe() {
    return { tier: this._tier, frames: this._L.frames, written: this._written, head: this._head, map: MAPS[this._map], amp: +this._DU.uAmp.value.toFixed(3), bytes: bufferBytes(this._tier), drop: !!this._drop, stats: Object.assign({}, this._stats) };
  }

  dispose() {
    if (this._scene) {
      if (this._display) { this._display.onBeforeRender = null; this._scene.remove(this._display); }
      this._scene.background = this._prevBackground || null;
    }
    for (const m of [this._display, this._srcQuad]) if (m) { m.geometry.dispose(); m.material.dispose(); }
    if (this._atlas) this._atlas.dispose();
    if (this._coverTex) this._coverTex.dispose();
    if (this._fallback) this._fallback.dispose();
    if (typeof window !== 'undefined' && window.__slitscan === this) window.__slitscan = null;
    this._scene = null;
  }
}

const easeInOut = (u) => { const x = clamp01(u); return x * x * (3 - 2 * x); };
